import React, { useEffect, useMemo, useState } from 'react';
import { FaCheckCircle, FaDownload, FaExclamationTriangle, FaFileExcel, FaUpload } from 'react-icons/fa';
import api from '../../api';
import useApi from '../../hooks/useApi';

function DemoDataManagement() {
  const { get, post } = useApi();
  const [communities, setCommunities] = useState([]);
  const [communityId, setCommunityId] = useState('');
  const [file, setFile] = useState(null);
  const [confirmation, setConfirmation] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState('');

  useEffect(() => {
    get('/api/communities')
      .then(items => {
        setCommunities(items || []);
        if (items?.length === 1) setCommunityId(String(items[0].id));
      })
      .catch(requestError => setError(requestError.message));
  }, [get]);

  const selectedCommunity = useMemo(
    () => communities.find(item => String(item.id) === String(communityId)),
    [communities, communityId]
  );

  const download = async () => {
    if (!communityId) return setError('Select the demo community first.');
    setBusy('download'); setError(''); setResult(null);
    try {
      const response = await api.get('/api/demo-data/workbook', { params: { communityId }, responseType: 'blob' });
      const url = window.URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${(selectedCommunity?.name || 'community').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}-demo-data.xlsx`;
      document.body.appendChild(link); link.click(); link.remove(); window.URL.revokeObjectURL(url);
    } catch (requestError) {
      setError(requestError.response?.data?.message || 'The workbook could not be downloaded. Please try again.');
    } finally { setBusy(''); }
  };

  const submit = async action => {
    if (!communityId) return setError('Select the demo community first.');
    if (!file) return setError('Choose the completed .xlsx workbook.');
    if (action === 'import' && confirmation !== 'DEMO_IMPORT') return setError('Type DEMO_IMPORT exactly to confirm the import.');
    setBusy(action); setError(''); setResult(null);
    try {
      const data = new FormData(); data.append('file', file);
      const params = { communityId, ...(action === 'import' ? { confirmation } : {}) };
      const response = await post(`/api/demo-data/${action}`, data, { params });
      setResult(response);
      if (response.imported) setConfirmation('');
    } catch (requestError) { setError(requestError.message); }
    finally { setBusy(''); }
  };

  return (
    <section className="demo-data-page">
      <header className="demo-data-intro">
        <span className="demo-data-icon"><FaFileExcel /></span>
        <div><span className="section-kicker">Demo preparation</span><h2>Demo data workbook</h2><p>Create a realistic demonstration community using a controlled, reviewable spreadsheet import.</p></div>
      </header>

      <div className="demo-data-warning"><FaExclamationTriangle /><div><strong>Use a dedicated demo community only.</strong><span>The import creates accounts and operational records. It does not delete or replace existing customer data.</span></div></div>
      {error && <div className="alert alert-danger">{error}</div>}
      {result && <div className={`alert ${result.valid ? 'alert-success' : 'alert-danger'}`}>{result.message}</div>}

      <div className="demo-data-steps">
        <article>
          <span>1</span><h3>Select community</h3>
          <label>Demo community<select value={communityId} onChange={event => { setCommunityId(event.target.value); setResult(null); }}><option value="">Choose a community</option>{communities.map(item => <option key={item.id} value={item.id}>{item.name} — {item.area}, {item.city}</option>)}</select></label>
          <small>The downloaded workbook and every imported row are tied to this community.</small>
        </article>
        <article>
          <span>2</span><h3>Download and prepare</h3>
          <p>Use the populated template as-is for a quick demo, or edit individual service tabs before validation.</p>
          <button type="button" className="btn btn-outline-primary" disabled={Boolean(busy)} onClick={download}><FaDownload /> {busy === 'download' ? 'Preparing…' : 'Download populated workbook'}</button>
        </article>
        <article>
          <span>3</span><h3>Validate workbook</h3>
          <label>Completed workbook<input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" onChange={event => { setFile(event.target.files?.[0] || null); setResult(null); }} /></label>
          <button type="button" className="btn btn-outline-primary" disabled={Boolean(busy)} onClick={() => submit('validate')}><FaCheckCircle /> {busy === 'validate' ? 'Validating…' : 'Validate without importing'}</button>
        </article>
        <article>
          <span>4</span><h3>Confirm import</h3>
          <label>Confirmation phrase<input value={confirmation} onChange={event => setConfirmation(event.target.value)} placeholder="Type DEMO_IMPORT" autoComplete="off" /></label>
          <button type="button" className="btn btn-primary" disabled={Boolean(busy) || confirmation !== 'DEMO_IMPORT'} onClick={() => submit('import')}><FaUpload /> {busy === 'import' ? 'Importing…' : 'Import demo data'}</button>
        </article>
      </div>

      {result && (
        <div className="demo-data-results">
          <h3>Workbook review</h3>
          <div className="demo-data-counts">{Object.entries(result.rowCounts || {}).map(([sheet, count]) => <span key={sheet}><strong>{count}</strong>{sheet}</span>)}</div>
          {result.warnings?.length > 0 && <div><h4>Warnings</h4><ul>{result.warnings.map(item => <li key={item}>{item}</li>)}</ul></div>}
          {result.errors?.length > 0 && <div><h4>Fix before importing</h4><ul>{result.errors.map(item => <li key={item}>{item}</li>)}</ul></div>}
        </div>
      )}
    </section>
  );
}

export default DemoDataManagement;
